import { useEffect, useRef, useCallback } from "react";

type NotifyOptions = {
  title: string;
  body: string;
  tag?: string;
  icon?: string;
  url?: string;
  requireInteraction?: boolean;
};

/** Request permission & register SW; returns a notify function */
export function useNotifications() {
  const permissionRef = useRef<NotificationPermission>("default");
  const swRef = useRef<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (!("Notification" in window)) return;
    permissionRef.current = Notification.permission;

    // Register lightweight SW for notification click handling
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw-notifications.js")
        .then((reg) => { swRef.current = reg; })
        .catch(() => {});
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return false;
    if (Notification.permission === "granted") {
      permissionRef.current = "granted";
      return true;
    }
    const result = await Notification.requestPermission();
    permissionRef.current = result;
    return result === "granted";
  }, []);

  const notify = useCallback(({ title, body, tag, icon, url, requireInteraction }: NotifyOptions) => {
    if (permissionRef.current !== "granted") return;

    const options: NotificationOptions = {
      body,
      tag, // de-duplicates same tag
      icon: icon || "/favicon.ico",
      badge: "/favicon.ico",
      requireInteraction: requireInteraction ?? false,
      data: { url },
    };

    // Use SW registration if available (works when tab is background)
    if (swRef.current) {
      swRef.current.showNotification(title, options).catch(() => {
        new Notification(title, options);
      });
    } else {
      new Notification(title, options);
    }
  }, []);

  // Play a beep sound
  const beep = useCallback(() => {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
      // Second beep
      const osc2 = ctx.createOscillator();
      osc2.connect(gain);
      osc2.frequency.value = 1100;
      osc2.start(ctx.currentTime + 0.3);
      osc2.stop(ctx.currentTime + 0.5);
    } catch {}
  }, []);

  return { requestPermission, notify, beep, permission: permissionRef };
}
